import { Editor } from '@tldraw/tldraw'
import { escape } from 'lodash'
import { compressToBase64 } from 'lz-string'
import { TextBoxShape } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { parseRichText } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/richTextDoc'

const readableText = (shape: TextBoxShape): string => {
  const doc = parseRichText(shape.props.richText)
  if (!doc) return shape.props.text

  const lines: string[] = []
  doc.descendants((node) => {
    if (!node.isTextblock) return true
    lines.push(node.textContent)
    return false
  })
  return lines.join('\n')
}

export const copyTextShapes = async (editor: Editor) => {
  const content = editor.getContentFromCurrentPage(editor.getSelectedShapeIds())
  if (!content) return

  const text = content.shapes
    .filter((shape): shape is TextBoxShape => shape.type === 'textBox')
    .map(readableText)
    .join('\n')

  if (!navigator.clipboard?.write) {
    await navigator.clipboard.writeText(text)
    return
  }

  const encodedContent = compressToBase64(
    JSON.stringify({ type: 'application/tldraw', kind: 'content', data: content }),
  )
  // Keep editor data out of visible text without relying on hidden nodes or HTML comments.
  const html = `<p data-blaze-text-shapes="${encodedContent}">${escape(text).replace(/\n/g, '<br>')}</p>`

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Blob([text || ' '], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' }),
    }),
  ])
}

export const prepareTextShapesForPaste = async (items: ClipboardItem[]) =>
  Promise.all(
    items.map(async (item) => {
      if (!item.types.includes('text/html')) return item

      const html = await (await item.getType('text/html')).text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const encodedContent = doc.querySelector('[data-blaze-text-shapes]')?.getAttribute('data-blaze-text-shapes')
      if (!encodedContent) return item

      // Adapt our text-friendly HTML to the existing tldraw decoder without changing other paste formats.
      return new ClipboardItem({
        'text/html': new Blob([`<tldraw>${encodedContent}</tldraw>`], { type: 'text/html' }),
      })
    }),
  )
